// ⬇ 전역 다운로드 진행률 카드 (2026-07-09) — rcClient.downloadViaBlob 이 'tpt:download-progress' 로 발행.
// 우하단 스택(최대 4개), 완료 1.5초/실패 3.5초/취소 1.2초 후 자동 소멸. content-length 없으면 MB 카운트 + 펄스.
// 진행 중에는 ✕(취소) 버튼 — cancelDownload(key) 가 스트림을 끊고 쓰다 만 파일을 정리한다 (2026-08-12 사용자).
// App 루트에 마운트 — 채팅/드라이브/다운로드 페이지 등 모든 호출처 공통.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './icons';
import { cancelDownload, type DlProgressDetail } from '../api/rcClient';

/** ⏱ 속도 계산 — 진행 이벤트(150ms 스로틀)의 (시각, 받은 바이트) 표본을 키별로 쌓고 최근 3초 창의 기울기(bytes/s) 를 낸다.
 *  발사 쪽(rcClient)은 그대로 — 표본은 화면에서만 (2026-09-16 사용자 "% 도 나오는데 속도도 나오게"). 반환 null = 표본 부족. */
const SPEED_WINDOW_MS = 3000;
export function speedOf(samples: ReadonlyArray<{ t: number; loaded: number }>): number | null {
	if (samples.length < 2) return null;
	const last = samples[samples.length - 1];
	let first = samples[0];
	for (const s of samples) { if (last.t - s.t <= SPEED_WINDOW_MS) { first = s; break; } }
	const dt = (last.t - first.t) / 1000;
	if (dt <= 0.2) return null;
	return Math.max(0, (last.loaded - first.loaded) / dt);
}
export function fmtSpeed(bps: number): string {
	if (bps >= 1024 * 1024) return `${(bps / 1024 / 1024).toFixed(1)}MB/s`;
	if (bps >= 1024) return `${Math.round(bps / 1024)}KB/s`;
	return `${Math.round(bps)}B/s`;
}
export function fmtEta(sec: number): string {
	if (!Number.isFinite(sec) || sec < 0) return '';
	if (sec < 60) return `${Math.max(1, Math.round(sec))}s`;
	if (sec < 3600) return `${Math.floor(sec / 60)}m ${Math.round(sec % 60)}s`;
	return `${Math.floor(sec / 3600)}h ${Math.round((sec % 3600) / 60)}m`;
}

export default function DownloadProgress() {
	const { t } = useTranslation();
	const [items, setItems] = useState<DlProgressDetail[]>([]);
	const samplesRef = useRef<Map<number, Array<{ t: number; loaded: number }>>>(new Map());
	const [speeds, setSpeeds] = useState<Record<number, number | null>>({});

	useEffect(() => {
		const h = (e: Event) => {
			const d = (e as CustomEvent<DlProgressDetail>).detail;
			if (!d || typeof d.key !== 'number') return;
			setItems((prev) => [...prev.filter((x) => x.key !== d.key), d].slice(-4));
			// 속도 표본 — 진행 중에만 쌓고 최근 40개(≈6초)만 남긴다
			if (!d.done) {
				const arr = samplesRef.current.get(d.key) || [];
				arr.push({ t: Date.now(), loaded: d.loaded });
				if (arr.length > 40) arr.splice(0, arr.length - 40);
				samplesRef.current.set(d.key, arr);
				setSpeeds((prev) => ({ ...prev, [d.key]: speedOf(arr) }));
			} else {
				samplesRef.current.delete(d.key);
				setSpeeds((prev) => { const n = { ...prev }; delete n[d.key]; return n; });
			}
			if (d.done) {
				window.setTimeout(() => setItems((prev) => prev.filter((x) => x.key !== d.key)), d.error ? 3500 : d.canceled ? 1200 : 1500);
			}
		};
		window.addEventListener('tpt:download-progress', h as EventListener);
		return () => window.removeEventListener('tpt:download-progress', h as EventListener);
	}, []);

	if (items.length === 0) return null;
	const mb = (n: number) => (n / 1024 / 1024).toFixed(1);
	return (
		// 모바일은 하단 탭바(약 64px)+safe-area 위로 올려 탭(조직도/더보기)을 가리지 않게. 데스크톱은 bottom-4 (2026-07-18)
		<div className="pointer-events-none fixed right-4 z-[1500] flex w-[min(320px,90vw)] flex-col gap-2 bottom-[calc(env(safe-area-inset-bottom)+72px)] sm:bottom-4" data-testid="download-progress">
			{items.map((it) => {
				const pct = it.total > 0 ? Math.min(100, Math.round((it.loaded / it.total) * 100)) : null;
				const bps = speeds[it.key] ?? null;
				const eta = bps && bps > 0 && it.total > it.loaded ? (it.total - it.loaded) / bps : null;
				// 진행 중 부제 — "123.4 / 512.0MB · 12.3MB/s · 남은 32s" (총량 미상이면 받은 양·속도만)
				const sub = !it.done ? [
					it.total > 0 ? `${mb(it.loaded)} / ${mb(it.total)}MB` : `${mb(it.loaded)}MB`,
					bps !== null ? fmtSpeed(bps) : null,
					eta !== null ? t('download.dlEta', { defaultValue: '남은 {{time}}', time: fmtEta(eta) }) : null,
				].filter(Boolean).join(' · ') : '';
				return (
					<div key={it.key} className="pointer-events-auto rounded-xl border border-line bg-surface px-3.5 py-2.5 shadow-xl">
						<div className="flex items-center gap-2 text-ui-xs">
							<span className={`shrink-0 ${it.error ? 'text-red-500' : it.canceled ? 'text-fg-faint' : 'text-class-blue'}`}><Icon name="download" size={14} /></span>
							<span className="min-w-0 flex-1 truncate font-medium text-fg">{it.name}</span>
							<span className="shrink-0 tabular-nums text-fg-subtle">
								{it.error ? t('download.dlFailed')
									: it.canceled ? t('download.dlCanceled', { defaultValue: '취소됨' })
										: it.done ? t('download.dlDone')
											: pct !== null ? `${pct}%` : `${mb(it.loaded)}MB`}
							</span>
							{!it.done && (
								<button type="button" onClick={() => cancelDownload(it.key)} data-testid="download-cancel"
									title={t('download.dlCancel', { defaultValue: '다운로드 취소' })}
									aria-label={t('download.dlCancel', { defaultValue: '다운로드 취소' })}
									className="-mr-1 shrink-0 rounded p-1 text-fg-faint hover:bg-hover hover:text-fg">
									<Icon name="x" size={12} />
								</button>
							)}
						</div>
						<div className="mt-1 h-1.5 overflow-hidden rounded-full bg-class-blue/15">
							<div className={`h-full rounded-full transition-[width] duration-150 ${it.error ? 'bg-red-400' : it.canceled ? 'bg-surface-stronger' : 'bg-class-blue'} ${!it.done && pct === null ? 'animate-pulse' : ''}`}
								style={{ width: it.done ? '100%' : pct !== null ? `${pct}%` : '40%' }} />
						</div>
						{sub && <div className="mt-1 truncate text-ui-xs tabular-nums text-fg-faint" data-testid="download-speed">{sub}</div>}
					</div>
				);
			})}
		</div>
	);
}
